import dayjs, { type Dayjs } from "dayjs";
import { ConfigurationType, type ICondition, type IServiceInterval, type IServiceSchedule, type IVehicle, type IVehicleCondition } from "@domain/Vehicle";
import { ServiceCondition } from "@domain/Vehicle/ConditionTypes/ServiceCondition";
import { useRouter } from '@library/Services';
import { useConfirm, useNotify } from "@library/Components";
import { useViewModelInstance } from "@app/MyGarage";

export class ConfigureConditionViewModel
{

    vehicle: IVehicle;
    condition: ICondition;
    vehicleCondition: IVehicleCondition;
    errorCodes: string[];
    selected: Dayjs;
    today = dayjs();
    labels = {
        date: '',
        timespan: ''
    };

    constructor( condition: ICondition, vehicle: IVehicle, errorCodes?: string[] )
    {
        this.vehicle = vehicle;
        this.condition = condition;
        this.vehicleCondition = vehicle.condition;
        this.errorCodes = errorCodes ?? [];
        this.selected = dayjs();
        this.initialise();
    }

    /**
     * Initialises the model
     */
    private initialise()
    {
        this.onSelectLastDone( this.selected, false );
    }

    /**
     * True if the view model has error codes and is therefore
     * in a critical state, even if the condition itself is not marked as critical,
     * or the condition is in a critical state
     */
    get isCritical()
    {
        return this.condition.isCritical || this.errorCodes.length > 0;
    }

    /**
     * Gets a range of dates, one month apart, from and including the current month
     * @param count The number of dates to return 
     * @returns 
     */
    getFutureMonthlyRange( startDate: Dayjs, count: number )
    {

        const today = dayjs();
        const earliest = startDate.isBefore( today ) ? startDate : today;
        const offset = earliest.month() % 4;
        const normalisedStartDate = earliest.subtract( offset, 'month' );
        const additionalMonths = earliest.month() == 0 ? 0 : 12 - normalisedStartDate.month();
        const range = Array.from( Array( count + additionalMonths ).keys() );
        const result = range.map( m => normalisedStartDate.add( m, 'month' ) );
        return result;
    }

    /**
     * Gets a range of dates, one month apart, up to and including the current month
     * @param count The number of dates to return 
     * @returns 
     */
    getPastMonthlyRange( count: number )
    {
        const range = Array.from( Array( count ).keys() ); // array of indexes
        return range.map( m => dayjs( new Date( dayjs().year(), 11, 1 ) ).subtract( m, 'month' ) );
    }
    
    /**
     * Occurs when the user selects a month when the work was last done
     */
    async onSelectLastDone( date: Dayjs, confirm = true )
    {

        this.selected = date;

        const monthsBack = dayjs().diff( date, 'month' );
        if( monthsBack == 0 )
        {
            this.labels.timespan = 'I just had this done';
            this.labels.date = dayjs().format( 'MMMM YYYY' );
            return;
        } 

        const monthsAgo = monthsBack + ' month' + ( monthsBack != 1 ? 's' : '' );
        this.labels.timespan = `About ${ monthsAgo } ago`;  
        this.labels.date = date.format( 'MMMM YYYY');

        // Ask the user to confirm
        if( confirm )
        {
            const result = await useConfirm( `Your ${this.condition.labels.name} was last done in ${date.format( 'MMMM YYYY')}.  Is this correct?` );
            if( result ) await this.setNextDate( date );
        }

    }

    /**
     * Occurs when the user selects the next date for the reminder
     */
    async onSelectNext( date: Dayjs, data?: any )
    {

        this.selected = date;

        // Ask the user to confirm
        if( this.condition.configurationType == ConfigurationType.Weekly )
        {
            const label = `${data} week${ data != 1 ? 's' : ''}`;
            const result = await useConfirm( `You will be reminded again in ${label}.\nIs this OK?`, '' );
            if( result ) await this.setNextDate( date, true );
        }

        else if( this.condition.configurationType == ConfigurationType.Monthly )
        {
            const result = await useConfirm( `You will be reminded again in ${date.format( 'MMMM YYYY')}.\nIs this OK?`, '' );
            if( result ) await this.setNextDate( date );
        }

        else if( this.condition.configurationType == ConfigurationType.Date )
        {
            const result = await useConfirm( `Your next reminder is ${date.format( 'Do MMMM YYYY')}.\nIs this OK?`, '' );
            if( result ) await this.setNextDate( date, true );
        }

        return Promise.resolve( true );

    }
    
    /**
     * Occurs when the service is set to a new interval off the service schedule
     * @param interval 
     */
    async setServiceInterval( interval: IServiceInterval, schedule: IServiceSchedule )
    {
        const viewModel = await useViewModelInstance();
        await viewModel.onConditionSetServiceIntervalAsync( this.condition, interval, schedule );

        this.vehicleCondition.serviceSchedule.nextInterval = interval;

        this.vehicleCondition.conditions.get( ServiceCondition ).setServiceSchedule( schedule );
    }

    /**
     * Occurs once the user confirms the adjustment. Save the settings
     * and perform a back action
     * @param nextDate 
     * @param advanceFromToday 
     */
    private async setNextDate( nextDate: any, advanceFromToday: boolean = false )
    {
        const router = useRouter();
        const viewModel = await useViewModelInstance();
        await viewModel.onConditionSetNextAsync( this.condition, nextDate, advanceFromToday );
        router.vueRouter.go( -1 );
    
    }

    /**
     * Marks the condition as done and moves it to its next position
     */
    async markDone()
    {
        const router = useRouter();
        const viewModel = await useViewModelInstance();
        await viewModel.onConditionMarkDoneAsync( this.condition );
        await useNotify( `Your ${ this.condition.labels.maintenanceVerb } has been marked as done.\n\nYour next ${ this.condition.labels.maintenanceVerb } reminder will be roughly ${ this.condition.labels.intervalState }`, '' );
        router.vueRouter.go( -1 );
    }

    /**
     * Marks a critical condition as resolved
     */
    async resolve()
    {
        const router = useRouter();
        const viewModel = await useViewModelInstance();
        await viewModel.onConditionResolveAsync( this.condition );
        router.vueRouter.go( -1 );
    }

    /**
     * Marks a critical condition as dismissed by the user
     */
    async dismiss()
    {
        const router = useRouter();
        const viewModel = await useViewModelInstance();
        await viewModel.onConditionDismissAsync( this.condition );
        router.vueRouter.go( -1 );
    }

}